// pages/BatchReports/BatchReportsDocumentationPage.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Box,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import GetAppIcon from "@material-ui/icons/GetApp";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import PageTitle from "components/PageTitle/PageTitle";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import useStyles from "./styles";
import { dbGet, dbPutWithFormData } from "utils/DBFetchers";

const MANIFEST_ENDPOINT = "cli/batch/report_manifest";

function normCategory(cat) {
  return (cat ?? "").toString().trim().toLowerCase();
}
function displayCategory(catNorm) {
  return catNorm ? catNorm : "sin categoría";
}
function normText(v) {
  return (v ?? "").toString().trim().toLowerCase();
}

function getApiBaseUrl() {
  // 1) CRA: REACT_APP_API_URL
  // 2) fallback relativo (misma origin)
  return process.env.REACT_APP_API_URL || process.env.REACT_APP_BASE_URL || "";
}

function officeEmbedUrl(rawUrl) {
  const encoded = encodeURIComponent(rawUrl);
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encoded}`;
}

export default function BatchReportsDocumentationPage() {
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);

  const [manifestObj, setManifestObj] = useState({});
  const [etag, setEtag] = useState("");

  const [selectedCategoryNorm, setSelectedCategoryNorm] = useState("");
  const [search, setSearch] = useState("");
  const [selectedCmdName, setSelectedCmdName] = useState("");

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [toast, setToast] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  function showToast(severity, message) {
    setToast({ open: true, severity, message });
  }
  function closeToast() {
    setToast((t) => ({ ...t, open: false }));
  }

  const loadManifest = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dbGet(MANIFEST_ENDPOINT);
      const obj = data?.report_manifest_json || {};
      setManifestObj(obj);
      setEtag(data?.etag || "");
    } catch (e) {
      showToast("error", "No se pudo cargar el report manifest.");
      setManifestObj({});
      setEtag("");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadManifest();
  }, [loadManifest]);

  const { categories, items } = useMemo(() => {
    const entries = Object.entries(manifestObj || {});
    const mapped = entries.map(([cmdName, cfg]) => {
      const catNorm = normCategory(cfg?.category);
      const alias = cfg?.alias || cmdName;
      const docUrl = (cfg?.docx_documentation_url ?? "").toString().trim();
      const hasDoc = Boolean(docUrl);
      return {
        cmdName,
        cfg,
        catNorm,
        alias,
        aliasNorm: normText(alias),
        cmdNorm: normText(cmdName),
        hasDoc,
        docUrl,
      };
    });

    const cats = Array.from(new Set(mapped.map((x) => x.catNorm))).sort((a, b) =>
      a.localeCompare(b)
    );

    mapped.sort((a, b) => {
      const c = a.catNorm.localeCompare(b.catNorm);
      if (c !== 0) return c;
      return a.aliasNorm.localeCompare(b.aliasNorm);
    });

    return { categories: cats, items: mapped };
  }, [manifestObj]);

  // Autoselección de categoría
  useEffect(() => {
    if (!selectedCategoryNorm && categories.length > 0) {
      setSelectedCategoryNorm(categories[0]); // puede ser ""
    }
  }, [categories, selectedCategoryNorm]);

  const filteredItems = useMemo(() => {
    const q = normText(search);
    return items.filter((it) => {
      const okCat = it.catNorm === selectedCategoryNorm;
      if (!okCat) return false;
      if (!q) return true;
      return it.aliasNorm.includes(q) || it.cmdNorm.includes(q);
    });
  }, [items, selectedCategoryNorm, search]);

  // Si el cmd seleccionado deja de estar en la lista filtrada, lo deseleccionamos
  useEffect(() => {
    if (!selectedCmdName) return;
    const exists = filteredItems.some((it) => it.cmdName === selectedCmdName);
    if (!exists) {
      setSelectedCmdName("");
      setSelectedFile(null);
      setIsPreviewOpen(false);
    }
  }, [filteredItems, selectedCmdName]);

  const selected = useMemo(() => {
    if (!selectedCmdName) return null;
    const cfg = manifestObj?.[selectedCmdName] || null;
    if (!cfg) return null;
    const alias = cfg?.alias || selectedCmdName;
    const docUrl = (cfg?.docx_documentation_url ?? "").toString().trim();
    return {
      cmdName: selectedCmdName,
      alias,
      categoryNorm: normCategory(cfg?.category),
      description: cfg?.description || "",
      docUrl,
      hasDoc: Boolean(docUrl),
      iframeSrc: docUrl ? officeEmbedUrl(docUrl) : "",
    };
  }, [manifestObj, selectedCmdName]);

  async function onReload() {
    const ok = window.confirm("¿Recargar desde el servidor?");
    if (!ok) return;

    setReloading(true);
    try {
      await loadManifest();
      showToast("info", "Manifest recargado.");
    } finally {
      setReloading(false);
    }
  }

  function onSelectCategory(e) {
    setSelectedCategoryNorm(e.target.value);
    setSearch("");
    setSelectedCmdName("");
    setSelectedFile(null);
    setIsPreviewOpen(false);
  }

  function onPickReport(cmdName) {
    setSelectedCmdName(cmdName);
    setSelectedFile(null);
    setIsPreviewOpen(false);
  }

  function validateDocxFile(file) {
    if (!file) return "Seleccioná un archivo.";
    const name = (file.name || "").toLowerCase();
    if (!name.endsWith(".docx")) return "El archivo debe ser .docx";
    const maxBytes = 20 * 1024 * 1024;
    if (file.size > maxBytes) return "El archivo excede 20MB.";
    return null;
  }

  async function onUpload() {
    if (!selected) {
      showToast("warning", "Seleccioná un reporte primero.");
      return;
    }

    const err = validateDocxFile(selectedFile);
    if (err) {
      showToast("warning", err);
      return;
    }

    const ok = window.confirm(
      `¿Subir nueva versión de documentación para:\n- ${selected.alias} (${selected.cmdName})?`
    );
    if (!ok) return;

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("docx_content", selectedFile, selectedFile.name);

      // ✅ usa DBFetchers (baseURL + Bearer)
      const resp = await dbPutWithFormData(
        `cli/batch/docx/${encodeURIComponent(selected.cmdName)}`,
        fd
      );

      // resp suele ser { docx_url: ... } según tu schema
      if (resp?.docx_url) {
        showToast("success", "Documentación actualizada.");
      } else {
        // igual OK, pero no vino url
        showToast("success", "Documentación actualizada.");
      }

      await loadManifest();
      setSelectedFile(null);
      setIsPreviewOpen(true);
    } catch (e) {
      const backendMsg =
        e?.errors?.description?.[0] ||
        e?.error ||
        (typeof e === "string" ? e : null) ||
        e?.message ||
        "Error al subir el DOCX.";

      showToast("error", backendMsg);
    } finally {
      setUploading(false);
    }
  }


  async function onCopyLink() {
    if (!selected?.hasDoc) return;
    try {
      await navigator.clipboard.writeText(selected.docUrl);
      showToast("success", "Link copiado.");
    } catch {
      showToast("warning", "No se pudo copiar el link.");
    }
  }

  const canUpload = Boolean(selected) && !loading && !reloading && !uploading;

  return (
    <Grid>
      <PageTitle title="Tareas - Documentación" />

      <Paper elevation={3} className={classes.paperContainer}>
        <Box p={2}>
          {/* Header */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            style={{ gap: 12 }}
          >
            <Box>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                Documentación de reportes
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Administrá los archivos DOCX por reporte. Manifest ETag: {etag || "—"}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Tooltip title="Recargar">
                <span>
                  <IconButton onClick={onReload} disabled={loading || reloading || uploading}>
                    {reloading ? <CircularProgress size={18} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Box mt={2}>
            <Divider />
          </Box>

          {/* Loading */}
          {loading ? (
            <Box mt={2} display="flex" alignItems="center" style={{ gap: 12 }}>
              <CircularProgress size={22} />
              <Typography>Cargando manifest...</Typography>
            </Box>
          ) : (
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              {/* Left column: list */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" style={{ borderRadius: 10 }}>
                  <Box p={2}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      style={{ gap: 12 }}
                    >
                      <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                        Reportes
                      </Typography>

                      <Chip
                        size="small"
                        label={`${filteredItems.length} item(s)`}
                        variant="outlined"
                      />
                    </Box>

                    <Box mt={2} display="flex" flexWrap="wrap" style={{ gap: 12 }}>
                      <FormControl variant="outlined" style={{ minWidth: 220 }} fullWidth>
                        <InputLabel>Categoría</InputLabel>
                        <Select
                          value={selectedCategoryNorm}
                          onChange={onSelectCategory}
                          label="Categoría"
                        >
                          {categories.map((catNorm) => (
                            <MenuItem key={catNorm || "__uncategorized__"} value={catNorm}>
                              {displayCategory(catNorm)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <TextField
                        variant="outlined"
                        label="Buscar"
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="alias o key"
                      />
                    </Box>

                    <Box mt={2}>
                      <Divider />
                    </Box>

                    <Box mt={2} style={{ maxHeight: 520, overflow: "auto" }}>
                      {filteredItems.length === 0 ? (
                        <Typography color="textSecondary">
                          No hay reportes para esta categoría/filtro.
                        </Typography>
                      ) : (
                        <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
                          {filteredItems.map((it) => {
                            const isSelected = it.cmdName === selectedCmdName;
                            return (
                              <Paper
                                key={it.cmdName}
                                variant="outlined"
                                onClick={() => onPickReport(it.cmdName)}
                                style={{
                                  cursor: "pointer",
                                  borderRadius: 10,
                                  border: isSelected ? "2px solid #3f51b5" : "1px solid #ddd",
                                  background: isSelected ? "#eef3fb" : "#fff",
                                }}
                              >
                                <Box
                                  p={1.5}
                                  display="flex"
                                  justifyContent="space-between"
                                  style={{ gap: 10 }}
                                >
                                  <Box style={{ minWidth: 0 }}>
                                    <Typography style={{ fontWeight: 700 }} noWrap>
                                      {it.alias}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                      style={{ fontFamily: "monospace" }}
                                      noWrap
                                    >
                                      {it.cmdName}
                                    </Typography>
                                  </Box>

                                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                    <Chip
                                      size="small"
                                      label={it.hasDoc ? "DOCX" : "Sin doc"}
                                      color={it.hasDoc ? "primary" : "default"}
                                      variant={it.hasDoc ? "default" : "outlined"}
                                    />
                                  </Box>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Right column: detail */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" style={{ borderRadius: 10 }}>
                  <Box p={2}>
                    <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                      Gestión
                    </Typography>

                    <Box mt={2}>
                      <Divider />
                    </Box>

                    {!selected ? (
                      <Box mt={2}>
                        <Typography color="textSecondary">
                          Seleccioná un reporte para ver/actualizar su documentación.
                        </Typography>
                      </Box>
                    ) : (
                      <Box mt={2} display="flex" flexDirection="column" style={{ gap: 14 }}>
                        <Box>
                          <Typography style={{ fontWeight: 700 }}>{selected.alias}</Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{ fontFamily: "monospace" }}
                          >
                            {selected.cmdName}
                          </Typography>

                          {selected.description ? (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              style={{ marginTop: 8, fontStyle: "italic" }}
                            >
                              {selected.description}
                            </Typography>
                          ) : null}
                        </Box>

                        {/* Archivo actual + Preview */}
                        <Paper variant="outlined" style={{ borderRadius: 10 }}>
                          <Box p={2}>
                            <Typography style={{ fontWeight: 700 }}>Archivo actual</Typography>

                            <Box mt={1}>
                              {selected.hasDoc ? (
                                <>
                                  <Typography variant="body2" color="textSecondary" noWrap>
                                    {selected.docUrl}
                                  </Typography>

                                  <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      startIcon={<GetAppIcon />}
                                      href={selected.docUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Descargar
                                    </Button>

                                    <Button
                                      variant="outlined"
                                      startIcon={<FileCopyIcon />}
                                      onClick={onCopyLink}
                                    >
                                      Copiar link
                                    </Button>
                                  </Box>

                                  {/* Preview: mismo estilo/patrón que BatchReportSubmitSection */}
                                  <Box mt={2}>
                                    <Accordion
                                      expanded={isPreviewOpen}
                                      onChange={(_, expanded) => setIsPreviewOpen(expanded)}
                                      style={{
                                        background: "#eef3fb",
                                        border: "1px solid #cfd9ea",
                                        borderRadius: 8,
                                        boxShadow: "none",
                                        overflow: "hidden",
                                      }}
                                    >
                                      <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        style={{ minHeight: 56 }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="space-between"
                                          width="100%"
                                          style={{ gap: 12 }}
                                        >
                                          <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                                            <Tooltip title="Visualización (solo lectura)">
                                              <DescriptionOutlinedIcon
                                                style={{ color: "#3f51b5", opacity: 0.9 }}
                                              />
                                            </Tooltip>
                                            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                              Documentación
                                            </Typography>
                                          </Box>

                                          <Button
                                            size="small"
                                            variant="text"
                                            style={{ textTransform: "none", fontSize: 13 }}
                                            startIcon={<OpenInNewIcon />}
                                            href={selected.iframeSrc}
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            Abrir en nueva pestaña
                                          </Button>
                                        </Box>
                                      </AccordionSummary>

                                      <AccordionDetails style={{ padding: "0 16px 16px 16px" }}>
                                        <div
                                          style={{
                                            border: "1px solid #cfd9ea",
                                            borderRadius: 8,
                                            overflow: "hidden",
                                            background: "#fff",
                                            height: 520,
                                            width: "100%",
                                          }}
                                        >
                                          <iframe
                                            title="Documentación - preview"
                                            src={selected.iframeSrc}
                                            width="100%"
                                            height="100%"
                                            style={{ border: 0 }}
                                          />
                                        </div>
                                      </AccordionDetails>
                                    </Accordion>
                                  </Box>
                                </>
                              ) : (
                                <Typography variant="body2" color="textSecondary">
                                  Sin documentación cargada.
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Paper>

                        {/* Upload */}
                        <Paper variant="outlined" style={{ borderRadius: 10 }}>
                          <Box p={2}>
                            <Typography style={{ fontWeight: 700 }}>Subir nueva versión</Typography>

                            <Box mt={1} display="flex" flexDirection="column" style={{ gap: 10 }}>
                              <input
                                type="file"
                                accept=".docx"
                                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                                disabled={!canUpload}
                              />

                              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  startIcon={
                                    uploading ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <CloudUploadIcon />
                                    )
                                  }
                                  onClick={onUpload}
                                  disabled={!canUpload || !selectedFile}
                                >
                                  Subir
                                </Button>

                                <Typography variant="caption" color="textSecondary">
                                  {selectedFile ? selectedFile.name : "Elegí un .docx"}
                                </Typography>
                              </Box>

                              <Typography variant="caption" color="textSecondary">
                                Al subir, se actualiza el manifest y el link queda disponible para descarga.
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      </Paper>

      <CustomizedSnackbars
        open={toast.open}
        severity={toast.severity}
        message={toast.message}
        onClose={closeToast}
      />
    </Grid>
  );
}
